/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire application.
 * 
 * Maritime Context:
 * - Critical for ship operations (errors shouldn't halt entire system)
 * - Graceful degradation allows continued access to other modules
 * - Error reporting for debugging and improvement
 * 
 * Features:
 * - User-friendly error UI
 * - Error details for debugging (dev only)
 * - Automatic error reporting (Sentry integration point)
 * - Reset functionality
 * - Context preservation where possible
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { createLogger } from '../../lib/utils/logger';

const logger = createLogger('ErrorBoundary');

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  // Future: Sentry DSN for error reporting
  sentryDsn?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so next render shows fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details
    logger.error('React Error Boundary caught error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Update state with error info
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Future: Send to Sentry or other error tracking service
    // if (this.props.sentryDsn) {
    //   Sentry.captureException(error, {
    //     contexts: {
    //       react: {
    //         componentStack: errorInfo.componentStack,
    //       },
    //     },
    //   });
    // }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      const isDevelopment = import.meta.env.DEV;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Something Went Wrong
            </h1>

            {/* User-friendly message */}
            <p className="text-gray-600 text-center mb-6">
              We're sorry, but something unexpected happened. The error has been logged
              and will be reviewed by our team. You can try refreshing the page or
              returning to the dashboard.
            </p>

            {/* Error Count Warning */}
            {this.state.errorCount > 1 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  ⚠️ This error has occurred {this.state.errorCount} times. You may want to
                  clear your browser cache or contact support.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Home className="w-5 h-5" />
                Go to Dashboard
              </button>
            </div>

            {/* Reload Option */}
            <div className="text-center">
              <button
                onClick={this.handleReload}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Reload Page
              </button>
            </div>

            {/* Error Details (Development Only) */}
            {isDevelopment && this.state.error && (
              <div className="mt-8 p-4 bg-gray-900 rounded-lg overflow-auto">
                <h3 className="text-white font-semibold mb-2">Error Details (Dev Only):</h3>
                <pre className="text-red-400 text-xs font-mono whitespace-pre-wrap">
                  {this.state.error.toString()}
                </pre>
                {this.state.error.stack && (
                  <pre className="text-gray-400 text-xs font-mono whitespace-pre-wrap mt-2">
                    {this.state.error.stack}
                  </pre>
                )}
                {this.state.errorInfo?.componentStack && (
                  <>
                    <h4 className="text-white font-semibold mt-4 mb-2">Component Stack:</h4>
                    <pre className="text-gray-400 text-xs font-mono whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            )}

            {/* Maritime Support Note */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-900 text-sm">
                <strong>⚓ Maritime Operations Note:</strong> If this error prevents
                critical operations (logbook entries, certificates, etc.), please contact
                support immediately. Other modules may still be accessible from the dashboard.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
